import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createLineKml, createPointsKML, createPolygonKml } from "@nova/data-create-shape-kml"
import { FeatureCollection } from "geojson"
import { useAppDispatch } from "integrations/redux/hooks"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { setGlobalNotificationContext } from "user/profile/slice"
import { stageKml } from "./module"
import { DownloadType, ParsedGeoLayers } from "./types"
import { lineTransformer } from "integrations/geojson/transformers/line"
import { polygonTransformer } from "integrations/geojson/transformers/polygon"
import { iconTransformer } from "integrations/geojson/transformers/icons"
import { downloadUriSameOrigin } from "tools/browser/download"
import JSZip from "jszip"
import { useCallback } from "react"
import { createCondensedDate } from "@nova/format"
import { GeometryType, SidePanelLayer, SidePanelLayerType } from "@nova/constants"
import { Icon, Line, Ruler } from "@nova/protobuf"
import { hideGlobalModal } from "runlevel/actions"
import { config } from "@nova/configuration/browser"

export const useExportLayers = () => {
    const dispatch = useAppDispatch()
    const { organizationSettingCRS } = config.use("organizationSettingCRS")
    const coordinateSystem = coordinateSystems[organizationSettingCRS]

    const createShp = useCallback(
        async (
            data: FeatureCollection,
            type: string,
            suffix: string,
            output: {
                blob: Blob
                name: string
            }[]
        ) => {
            const res = await fetch("/api/ogr2ogr?crs=" + coordinateSystem.code + "&type=" + type, {
                method: "POST",
                body: new File([new Blob([JSON.stringify(data)])], `${type}.geojson`),
            })

            if (res.status >= 300) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to create shapefile.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )

                return
            }

            const blob = await res.blob()

            output.push({
                blob,
                name: `${type}-${suffix}.zip`,
            })
        },
        [dispatch, coordinateSystem]
    )

    const handleDownloadClick = useCallback(
        async (
            projectName: string,
            selectedLayers: SidePanelLayer[],
            selectedFileTypes: Set<DownloadType>
        ) => {
            const files: { blob: Blob; name: string }[] = []
            const date = createCondensedDate()

            const isOneLayer = selectedLayers.length === 1

            // prototodo
            // "_someLayerName" | ""
            const layerNameSuffix = isOneLayer ? `_${selectedLayers[0].name}` : ""

            // The prefix of the file name, but not the extension. YYMMDDHHmmss_ProjectName | YYMMDDHHmmss
            const fileNameConstant = `${date}_${projectName ? `${projectName}` : ""}${layerNameSuffix}`

            // Categorize selected layers into like types. Eg. icons, lines.
            const layersByLayerType: Partial<Record<SidePanelLayerType, SidePanelLayer[]>> = {}
            selectedLayers.forEach((layer) => {
                layersByLayerType[layer.type] ??= []
                layersByLayerType[layer.type]?.push(layer)
            })

            const parsedLinesAndPolygons: ParsedGeoLayers = {
                lines: {
                    kml: [],
                    shp: [],
                },
                polygons: {
                    kml: [],
                    shp: [],
                },
            }

            // Separate lines and polygons.
            if (
                selectedFileTypes.has(DownloadType.lineKml) ||
                selectedFileTypes.has(DownloadType.lineShp)
            ) {
                const lines = layersByLayerType["line"] as Line[] | undefined

                lines?.forEach((line) => {
                    const shpShape = {
                        name: line.name,
                        points: line.coordinates.map(
                            ({ longitude, latitude }) => [longitude, latitude] as [number, number]
                        ),
                    }

                    if (line.geometryType === GeometryType.Polygon) {
                        parsedLinesAndPolygons.polygons.kml.push(line)
                        parsedLinesAndPolygons.polygons.shp.push(shpShape)
                    } else {
                        parsedLinesAndPolygons.lines.kml.push(line)
                        parsedLinesAndPolygons.lines.shp.push(shpShape)
                    }
                })
            }

            // Separate ruler lines and ruler polygons.
            if (
                selectedFileTypes.has(DownloadType.rulerKml) ||
                selectedFileTypes.has(DownloadType.rulerShp)
            ) {
                const rulers = layersByLayerType["ruler"] as Ruler[] | undefined

                rulers?.forEach((ruler) => {
                    const shpShape = {
                        name: ruler.name,
                        points: ruler.coordinates.map(
                            ({ longitude, latitude }) => [longitude, latitude] as [number, number]
                        ),
                    }

                    if (ruler.geometryType === GeometryType.Polygon) {
                        parsedLinesAndPolygons.polygons.kml.push({ ...ruler, holes: [] })
                        parsedLinesAndPolygons.polygons.shp.push(shpShape)
                    } else {
                        parsedLinesAndPolygons.lines.kml.push({ ...ruler, holes: [] })
                        parsedLinesAndPolygons.lines.shp.push(shpShape)
                    }
                })
            }

            // Lines, Polygons, Rulers - KML
            if (
                selectedFileTypes.has(DownloadType.lineKml) ||
                selectedFileTypes.has(DownloadType.rulerKml)
            ) {
                if (parsedLinesAndPolygons.lines.kml.length > 0) {
                    const name = `${fileNameConstant}${isOneLayer ? "" : "_Lines"}`
                    const linesKml = createLineKml(parsedLinesAndPolygons.lines.kml, name)
                    stageKml(linesKml, name, files)
                }

                if (parsedLinesAndPolygons.polygons.kml.length > 0) {
                    const name = `${fileNameConstant}${isOneLayer ? "" : "_Polygons"}`
                    const polygonsKml = createPolygonKml(parsedLinesAndPolygons.polygons.kml, name)
                    stageKml(polygonsKml, name, files)
                }
            }
            // Lines, Polygons, Rulers - SHP
            if (
                selectedFileTypes.has(DownloadType.lineShp) ||
                selectedFileTypes.has(DownloadType.rulerShp)
            ) {
                if (parsedLinesAndPolygons.lines.shp.length > 0) {
                    const linesGeoJson = lineTransformer(
                        parsedLinesAndPolygons.lines.shp,
                        coordinateSystem.proj4
                    )
                    await createShp(linesGeoJson, "Lines", fileNameConstant, files)
                }

                if (parsedLinesAndPolygons.polygons.shp.length > 0) {
                    const polygonsGeoJson = polygonTransformer(
                        parsedLinesAndPolygons.polygons.shp,
                        coordinateSystem.proj4
                    )
                    await createShp(polygonsGeoJson, "Polygons", fileNameConstant, files)
                }
            }

            // Icons - KML
            if (selectedFileTypes.has(DownloadType.iconKml)) {
                const icons = layersByLayerType["icon"] as Icon[] | undefined

                if (icons && icons.length > 0) {
                    const name = `${fileNameConstant}${isOneLayer ? "" : "_Points"}`
                    const iconsKml = createPointsKML(icons, name)
                    stageKml(iconsKml, name, files)
                }
            }
            // Icons - SHP
            if (selectedFileTypes.has(DownloadType.iconShp)) {
                const icons = layersByLayerType["icon"] as Icon[] | undefined

                if (icons && icons.length > 0) {
                    const pointsGeoJson = iconTransformer(icons, coordinateSystem.proj4)
                    await createShp(pointsGeoJson, "Points", fileNameConstant, files)
                }
            }

            /** @todo */
            // hotspot-heat-points
            // hotspot-heat-polygons
            // hotspot-path
            // fp
            // ortho-path

            // Download files to user.
            if (files.length === 1) {
                const url = URL.createObjectURL(files[0].blob)
                downloadUriSameOrigin(files[0].name, url)
            } else if (files.length > 1) {
                const zip = new JSZip()
                files.forEach((file) => {
                    zip.file(file.name, file.blob)
                })
                const zippedFiles = await zip.generateAsync({ type: "blob" })
                const url = URL.createObjectURL(zippedFiles)
                downloadUriSameOrigin(fileNameConstant, url)
            }

            dispatch(hideGlobalModal())
        },
        [createShp, dispatch, coordinateSystem]
    )

    return {
        createShp,
        handleDownloadClick,
    }
}
